<template>
  <v-dialog v-model="dialog" persistent max-width="520px" class="coupon-dialog">
    <v-card class="coupon-card" elevation="24">
      <!-- Header with gradient background -->
      <div class="coupon-header">
        <div class="header-content">
          <div class="coupon-icon">
            <v-icon size="28" color="white">mdi-ticket-percent</v-icon>
          </div>
          <h2 class="header-title">Coupon Code</h2>
          <v-btn 
            icon 
            @click="onClose" 
            class="close-btn"
            color="white"
            size="small"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Main Content -->
      <div class="coupon-content">
        <!-- Welcome Message -->
        <div class="welcome-section">
          <p class="welcome-text">
            Unlock your exclusive discount by revealing your coupon code below
          </p>
        </div>

        <!-- Terms & Conditions -->
        <div class="terms-section">
          <div class="terms-header">
            <v-icon size="16" color="#0a5b8a" class="mr-2">mdi-information-outline</v-icon>
            <span class="terms-title">Terms & Conditions</span>
          </div>
          <div class="terms-content">
            {{ showCouponcodes.description }}
          </div>
        </div>

        <!-- Coupon Code Section -->
        <div class="code-section">
          <div class="code-label">
            <v-icon size="18" color="#0a5b8a" class="mr-2">mdi-ticket-outline</v-icon>
            Your Coupon Code
          </div>
          
          <div class="code-container">
            <div class="code-field" :class="{ 'revealed': couponRevealed }">
              <div class="code-display">
                <span v-if="!couponRevealed" class="code-hidden">
                  ●●●●●●●●●●●●
                </span>
                <span v-else class="code-revealed">
                  {{ couponCode }}
                </span>
              </div>
              
              <div class="code-actions">
                <v-btn
                  v-if="!couponRevealed"
                  @click="showCoupon"
                  color="#0a5b8a"
                  variant="flat"
                  size="small"
                  class="reveal-btn"
                  :loading="loading"
                >
                  <v-icon size="16" class="mr-1">mdi-eye</v-icon>
                  Reveal
                </v-btn>
                
                <v-btn
                  v-else
                  @click="copyCouponCode"
                  color="#10b981"
                  variant="flat"
                  size="small"
                  class="copy-btn"
                >
                  <v-icon size="16" class="mr-1">mdi-content-copy</v-icon>
                  Copy
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <transition name="fade">
          <div v-if="message" class="message-section">
            <v-alert
              :type="couponRevealed ? 'success' : 'error'"
              variant="tonal"
              class="custom-alert"
              :icon="couponRevealed ? 'mdi-check-circle' : 'mdi-alert-circle'"
            >
              {{ message }}
            </v-alert>
          </div>
        </transition>
      </div>

      <!-- Footer Actions -->
      <v-card-actions class="footer-actions">
        <v-btn
          @click="onClose"
          variant="outlined"
          size="large"
          class="close-action-btn"
          block
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  components: {},
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    showCouponcodes: {
      type: Object
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      couponCode: "",
      couponRevealed: false
    };
  },
  methods: {
    ...mapActions("awards", {
      reedemCoupon: "reedemCoupon"
    }),
    onChange() {
      this.message = null;
    },
    async showCoupon() {
      if (this.showCouponcodes && this.showCouponcodes._id) {
        this.loading = true;
        const apiData = {
          award_id: this.showCouponcodes._id
        };

        try {
          const response = await this.reedemCoupon(apiData);
          if (response && response.couponCode) {
            this.couponCode = response.couponCode;
            this.couponRevealed = true;
            this.message = "Coupon code revealed successfully!";
          } else {
            throw new Error("Invalid response format");
          }
        } catch (error) {
          console.error("Error revealing coupon:", error);
          this.message = error.response?.data.message || error.message || "Failed to reveal coupon code";
        } finally {
          this.loading = false;
        }
        
        // Emit the couponRevealed value to the parent component
        this.$emit("coupon-revealed", this.couponRevealed);
        
        // Clear message after delay
        if (this.message) {
          setTimeout(() => {
            this.message = null;
          }, 3000);
        }
      }
    },
    async copyCouponCode() {
      if (!this.couponRevealed) return;

      try {
        await navigator.clipboard.writeText(this.couponCode);
        this.message = "Coupon code copied to clipboard!";
        setTimeout(() => {
          this.message = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy: ", err);
        this.message = "Failed to copy coupon code!";
        setTimeout(() => {
          this.message = null;
        }, 2000);
      }
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    })
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        // Reset state when dialog closes
        this.couponRevealed = false;
        this.couponCode = "";
        this.message = null;
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.coupon-dialog :deep(.v-overlay__content) {
  margin: 16px;
}

.coupon-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

/* Header Styles */
.coupon-header {
  background: linear-gradient(135deg, #0a5b8a, #1DA1F2);
  padding: 24px;
  position: relative;
}

.coupon-header::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 20px;
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 8px,
    rgba(255, 255, 255, 0.1) 8px,
    rgba(255, 255, 255, 0.1) 16px
  );
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.coupon-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.header-title {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
  letter-spacing: -0.025em;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

/* Content Styles */
.coupon-content {
  padding: 32px 24px;
}

.welcome-section {
  text-align: center;
  margin-bottom: 24px;
}

.welcome-text {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
}

/* Terms Section */
.terms-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 32px;
}

.terms-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.terms-title {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.terms-content {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* Code Section */
.code-section {
  margin-bottom: 24px;
}

.code-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  font-size: 16px;
}

.code-container {
  position: relative;
}

.code-field {
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  min-height: 64px;
}

.code-field.revealed {
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
  border-color: #10b981;
}

.code-display {
  flex: 1;
}

.code-hidden {
  font-family: 'Courier New', monospace;
  font-size: 18px;
  color: #9ca3af;
  letter-spacing: 2px;
}

.code-revealed {
  font-family: 'Courier New', monospace;
  font-size: 18px;
  font-weight: 600;
  color: #065f46;
  letter-spacing: 1px;
  background: linear-gradient(135deg, #10b981, #059669);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.code-actions {
  margin-left: 16px;
}

.reveal-btn, .copy-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  color: #ffffff !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
}

/* Message Section */
.message-section {
  margin-bottom: 16px;
}

.custom-alert {
  border-radius: 12px !important;
  font-weight: 500;
}

/* Footer */
.footer-actions {
  padding: 0 24px 24px !important;
}

.close-action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  height: 48px !important;
  border: 2px solid #e5e7eb !important;
}

.close-action-btn:hover {
  background: #f9fafb !important;
  border-color: #d1d5db !important;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .coupon-dialog :deep(.v-overlay__content) {
    margin: 8px;
  }
  
  .coupon-header {
    padding: 20px 16px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .coupon-content {
    padding: 24px 16px;
  }
  
  .code-field {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .code-actions {
    margin-left: 0;
  }
  
  .footer-actions {
    padding: 0 16px 20px !important;
  }
}

/* Dark mode support */
/* @media (prefers-color-scheme: dark) {
  .coupon-card {
    background: #1f2937 !important;
  }
  
  .welcome-text {
    color: #9ca3af;
  }
  
  .terms-section {
    background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
    border-color: #4b5563;
  }
  
  .terms-title {
    color: #f9fafb;
  }
  
  .terms-content {
    color: #d1d5db;
  }
  
  .code-label {
    color: #f9fafb;
  }
  
  .code-field {
    background: #374151;
    border-color: #4b5563;
  }
  
  .code-field.revealed {
    background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
    border-color: #10b981;
  }
} */
</style>